import { useRef } from "react";
import { useParams } from "react-router-dom";
import { NavigationConfirm } from "react-router-navigation-confirm";
import { FormikProps } from "formik";
import { useConfirm } from "material-ui-confirm";
import * as L from "leaflet";
import { useTranslation } from "react-i18next";

import { EmissionType } from "src/types";
import {
  useEmission,
  useSubstances,
  usePointSource,
  usePointSourceUpdateMutation,
  usePointSourceDeleteMutation,
} from "src/hooks";
import { useScenario } from "src/hooks/useScenario";
import { PointSourceOnMap } from "src/components/on-map/PointSourceOnMap";
import { ConfirmUnsavedDialog } from "src/components/dialogs/ConfirmUnsavedDialog";
import { PointSourceForm, PointSourceFormData } from "src/forms/PointSourceForm";
import { isReadOnly } from "src/utils/permission.utils";

interface Props {
  map: L.Map;
  onClose: (originAction?: "delete") => void;
}

export function PointSourceEmissionPointSourceEditPage(props: Props) {
  const { t } = useTranslation();
  const params = useParams<{
    caseId: string;
    scenarioId: string;
    emissionId: string;
    pointSourceId: string;
  }>();
  const { data: scenario } = useScenario(params.scenarioId);
  const { data: substances } = useSubstances();
  const { data: pointSource } = usePointSource(params.scenarioId, params.emissionId, params.pointSourceId);
  const { data: emission } = useEmission(params.scenarioId, params.emissionId, EmissionType.POINT_SOURCE);
  const confirm = useConfirm();

  const formRef = useRef<FormikProps<PointSourceFormData> | null>(null);

  const updateMutation = usePointSourceUpdateMutation(params.scenarioId, params.emissionId, params.pointSourceId);
  const deleteMutation = usePointSourceDeleteMutation(params.scenarioId, params.emissionId, params.pointSourceId);

  /**
   * Effects
   */

  /**
   * Functions
   */
  function isDirty() {
    return formRef.current?.dirty === true;
  }

  function handleDiscardChanges() {
    // setDirtyData({});
  }

  function handleSave(values: PointSourceFormData) {
    if (!pointSource) return Promise.reject();
    return updateMutation
      .mutateAsync({
        ...pointSource,
        ...values,
      })
      .then(() => props.onClose());
  }

  async function handleDelete() {
    if (!pointSource) return;

    confirm({
      confirmationButtonProps: { autoFocus: true },
      title: t("label.areYouSure"),
      description: t("confirm.deletePointSource"),
    })
      .then(deleteMutation.mutateAsync)
      .then(() => props.onClose("delete"));
  }

  return render();

  function render() {
    if (!scenario || !emission || !pointSource || !substances) return null;

    return (
      <>
        <PointSourceForm
          key={params.pointSourceId}
          title={t("label.pointSourceDetails")}
          data={pointSource}
          substances={substances}
          readOnly={isReadOnly(scenario.permissions.updateEmission)}
          isLoading={updateMutation.isLoading}
          onSubmit={handleSave}
          onClose={() => {
            handleDiscardChanges();
            props.onClose();
          }}
          onDelete={handleDelete}
          errors={updateMutation.error?.response?.data}
          formRef={formRef}
        />

        <PointSourceOnMap
          map={props.map}
          scenarioId={params.scenarioId}
          emissionId={params.emissionId}
          pointSourceId={params.pointSourceId}
        />

        {renderNavigationConfirm()}
      </>
    );
  }

  function renderNavigationConfirm() {
    return (
      <NavigationConfirm when={isDirty}>
        {({ onConfirm, onCancel }) => (
          <ConfirmUnsavedDialog
            open
            onDiscard={() => {
              handleDiscardChanges();
              onConfirm();
            }}
            onSave={() => {
              // submit form and go back to the form
              formRef.current?.submitForm();
              onCancel();
            }}
            onCancel={() => {
              onCancel();
            }}
          />
        )}
      </NavigationConfirm>
    );
  }
}
